<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import type {
    MpfInputBloodPressureProps,
    MpfInputBloodPressureEmits,
  } from './MpfInputBloodPressure.props';
  import { bloodPressureParts, mpfInputBloodPressureDefaults } from './MpfInputBloodPressure.props';
  // The sub-fields are NHS DS text inputs; those rules now live with MpfInputText,
  // not in the monolith, so load them here rather than relying on a sibling input.
  import '../MpfInputText/MpfInputText.scss';

  import MpfLabel from '../MpfLabel/MpfLabel.vue';
  import MpfHint from '../MpfHint/MpfHint.vue';

  const props = withDefaults(
    defineProps<MpfInputBloodPressureProps>(),
    mpfInputBloodPressureDefaults,
  );
  const emit = defineEmits<MpfInputBloodPressureEmits>();

  defineOptions({
    name: 'MpfInputBloodPressure',
  });

  // internal parts
  const systolic = ref('');
  const diastolic = ref('');

  const baseId = computed(() => props.id || props.name || 'mpf-blood-pressure');
  const systolicId = computed(() => `${baseId.value}-systolic`);
  const diastolicId = computed(() => `${baseId.value}-diastolic`);

  const hasErrors = computed(() => (props.errors?.length ?? 0) > 0);
  const hintId = computed(() => (props.hint ? `${baseId.value}-hint` : ''));
  const errorId = computed(() => (hasErrors.value ? `${baseId.value}-error` : ''));

  const describedBy = computed(() => {
    const ids = [hintId.value, errorId.value, props.ariaDescribedby].filter(Boolean);
    return ids.length ? ids.join(' ') : undefined;
  });

  const legendHeadingTag = computed(() =>
    props.legendHeadingLevel
      ? (`h${props.legendHeadingLevel}` as keyof HTMLElementTagNameMap)
      : 'div',
  );
  const legendHeadingSize = computed(() =>
    props.legendHeadingSize ? `mpf-fieldset__legend--${props.legendHeadingSize}` : null,
  );
  const legendWeight = computed(() =>
    props.legendFontWeight ? `mpf-fieldset__font-weight--${props.legendFontWeight}` : null,
  );

  watch(
    () => props.modelValue,
    (val) => {
      const parts = bloodPressureParts(val);
      systolic.value = parts.systolic;
      diastolic.value = parts.diastolic;
    },
    { immediate: true },
  );

  // build "systolic/diastolic" from the parts and emit
  function updateModelFromParts(sourceEvent?: Event) {
    const s = systolic.value;
    const d = diastolic.value;

    // all empty → clear
    if (!s && !d) {
      emit('update:modelValue', '');
      if (sourceEvent) emit('input', sourceEvent);
      return;
    }

    // incomplete → don't emit model updates
    if (!s || !d) {
      if (sourceEvent) emit('input', sourceEvent);
      return;
    }

    emit('update:modelValue', `${s}/${d}`);
    if (sourceEvent) emit('input', sourceEvent);
  }

  function readNumeric(event: Event) {
    const target = event.target as HTMLInputElement;
    const value = target.value.replace(/\D+/g, '');
    target.value = value;
    return value;
  }

  function onInputSystolic(event: Event) {
    systolic.value = readNumeric(event);
    updateModelFromParts(event);
  }

  function onInputDiastolic(event: Event) {
    diastolic.value = readNumeric(event);
    updateModelFromParts(event);
  }

  function onChange(event: Event) {
    updateModelFromParts();
    emit('change', event);
  }

  function onBlur(event: Event) {
    updateModelFromParts();
    emit('blur', event);
  }

  function onFocus(event: Event) {
    emit('focus', event);
  }
</script>

<template>
  <div class="mpf-form-group" :class="{ 'mpf-form-group--error': hasErrors }">
    <fieldset class="mpf-fieldset" role="group" :aria-describedby="describedBy">
      <legend class="mpf-fieldset__legend" :class="[legendHeadingSize, legendWeight]">
        <component :is="legendHeadingTag" class="mpf-fieldset__heading" :class="legendWeight">
          {{ label }}
        </component>
      </legend>

      <MpfHint v-if="hint" :id="hintId" :hint="hint" :sr-hint="srHint" />

      <div v-if="hasErrors" :id="errorId" class="mpf-error-message" role="alert" aria-live="polite">
        {{ errors![0] }}
      </div>

      <div class="mpf-blood-pressure">
        <div class="mpf-blood-pressure__item">
          <MpfLabel
            :for-id="systolicId"
            :label="systolicLabel"
            label-class="mpf-blood-pressure__label"
          />
          <input
            :id="systolicId"
            :name="`${baseId}-systolic`"
            class="mpf-input mpf-input--width-3 mpf-blood-pressure__input"
            :class="{ 'mpf-input--error': hasErrors && !systolic }"
            inputmode="numeric"
            maxlength="3"
            :value="systolic"
            :disabled="disabled"
            :required="required || undefined"
            @input="onInputSystolic"
            @change="onChange"
            @blur="onBlur"
            @focus="onFocus"
            data-private
          />
        </div>

        <div class="mpf-blood-pressure__item">
          <MpfLabel
            :for-id="diastolicId"
            :label="diastolicLabel"
            label-class="mpf-blood-pressure__label"
          />
          <input
            :id="diastolicId"
            :name="`${baseId}-diastolic`"
            class="mpf-input mpf-input--width-3 mpf-blood-pressure__input"
            :class="{ 'mpf-input--error': hasErrors && !diastolic }"
            inputmode="numeric"
            maxlength="3"
            :value="diastolic"
            :disabled="disabled"
            :required="required || undefined"
            @input="onInputDiastolic"
            @change="onChange"
            @blur="onBlur"
            @focus="onFocus"
            data-private
          />
        </div>
      </div>
    </fieldset>
  </div>
</template>

<style lang="scss" scoped>
  @use './MpfInputBloodPressure.scss';
</style>
